import { IApiRequestOptions } from '@import-io/doc-sdk';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import NotExists from '_/components/common/NotExists';
import { IProject } from '_/types/projects/project.types';
import zeusSdk from '_/utils/SDKs';
import React, { createContext, useContext, ReactNode, useMemo } from 'react';

import { useOrganization } from './OrganizationContext';

interface ProjectContextValue {
  projects: IProject[];
  selectedProject: IProject | null;
  selectedProjectSlug: string | null;
  isLoading: boolean;
  notFound: boolean;
  refetch: () => void;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

async function fetchOrgProjects(orgSlug: string): Promise<IProject[]> {
  try {
    const options: IApiRequestOptions = {
      queryParameters: {
        sort: 'name',
        sortDirection: 'ASC',
      },
      orgSlug: orgSlug,
    };
    const response = await zeusSdk.projects.listAll(options);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error fetching organization projects', error);
    return [];
  }
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ projectSlug?: string }>;
  const projectSlug = params.projectSlug;
  const { selectedOrganization, selectedOrgSlug } = useOrganization();

  const {
    data: projects = [],
    isLoading,
    refetch,
  } = useQuery<IProject[]>({
    queryKey: ['projects', selectedOrganization?.id],
    queryFn: () => fetchOrgProjects(selectedOrganization!.id),
    enabled: !!selectedOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const selectedProject = useMemo(() => {
    // Don't try to find projects for special route slugs
    if (!projectSlug || projectSlug === '_new') return null;
    return projects.find((project) => project.slug === projectSlug) || null;
  }, [projects, projectSlug]);

  // Determine if project is not found: when loading is done, we have a projectSlug, but no matching project
  const notFound = !isLoading && !!projectSlug && projectSlug !== '_new' && !selectedProject;

  const value: ProjectContextValue = {
    projects: projects,
    selectedProject: selectedProject,
    selectedProjectSlug: projectSlug || null,
    isLoading: isLoading,
    notFound: notFound,
    refetch: refetch,
  };

  // If project not found, render NotExists page
  if (notFound) {
    return <NotExists entityType="Project" backUrl={`/orgs/${selectedOrgSlug}/projects`} backLabel="Back to Projects" />;
  }

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

/**
 * Safe version of useProject that returns null when used outside ProjectProvider.
 * Use this when ProjectContext may not be available.
 */
export function useProjectSafe(): ProjectContextValue | null {
  const context = useContext(ProjectContext);
  return context === undefined ? null : context;
}
